/**
 * Button Component
 *
 * A versatile button component following atomic design principles.
 * Supports multiple variants, sizes, and states with accessibility features.
 */

import React, { forwardRef } from 'react';
import { cn } from '../../utils/cn';

// Button variants
export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'danger'
  | 'success'
  | 'gradient'
  | 'gradient-purple'
  | 'gradient-multi'
  | 'gradient-cyan-pink'
  | 'gradient-brand-cyan'
  | 'pill-outline'
  | 'pill-outline-brand'
  | 'dark'
  | 'light'
  | 'rounded-outline'
  | 'rounded-dark'
  | 'rounded-light'
  | 'link'
  | 'icon';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';
export type ButtonState = 'default' | 'loading' | 'disabled';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  state?: ButtonState;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  children: React.ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      state = 'default',
      loading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      className,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || state === 'disabled' || loading;

    // Base button classes - Exact HTML specifications
    const baseClasses = cn(
      'inline-flex items-center justify-center font-semibold rounded-lg',
      'transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      'shadow-sm hover:shadow-md active:shadow-sm',
      'hover:scale-[1.02] active:scale-[0.98]',
      fullWidth && 'w-full'
    );

    // Variant classes
    const variantClasses = {
      primary: cn(
        'bg-primary-500 text-white hover:bg-primary-600',
        'focus:ring-primary-500 shadow-primary',
        'active:bg-primary-700',
        'border-2 border-primary-400 hover:border-primary-500'
      ),
      secondary: cn(
        'bg-secondary-500 text-white hover:bg-secondary-600',
        'focus:ring-secondary-500',
        'active:bg-secondary-700'
      ),
      outline: cn(
        'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50',
        'focus:ring-primary-500 hover:border-gray-400',
        'dark:border-gray-600 dark:text-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700'
      ),
      ghost: cn(
        'text-gray-700 hover:text-gray-900 hover:bg-gray-100',
        'focus:ring-gray-500 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800'
      ),
      danger: cn(
        'bg-error-500 text-white hover:bg-error-600',
        'focus:ring-error-500 shadow-sm hover:shadow-lg',
        'active:bg-error-700'
      ),
      success: cn(
        'bg-success-500 text-white hover:bg-success-600',
        'focus:ring-success-500 shadow-sm hover:shadow-lg',
        'active:bg-success-700'
      ),
      gradient: cn('btn-gradient-brand'),
      'gradient-purple': cn('btn-gradient-purple'),
      'gradient-multi': cn('btn-gradient-multi'),
      'gradient-cyan-pink': cn('btn-gradient-cyan-pink'),
      'gradient-brand-cyan': cn('btn-gradient-brand-cyan'),
      'pill-outline': cn(
        'rounded-full border-2 border-primary-500 text-gray-900 dark:text-white bg-transparent',
        'hover:bg-primary-50 dark:hover:bg-gray-800 hover:border-primary-600 hover:shadow-md'
      ),
      'pill-outline-brand': cn(
        'rounded-50px border-2 border-primary-500 text-gray-900 dark:text-white bg-transparent',
        'hover:bg-primary-50 dark:hover:bg-gray-800 hover:border-primary-600 hover:shadow-md px-8 py-3'
      ),
      dark: cn(
        'bg-gray-900 text-white hover:bg-gray-800 focus:ring-gray-500 shadow-sm hover:shadow-lg rounded-lg'
      ),
      light: cn(
        'bg-gray-100 text-gray-900 hover:bg-gray-200 focus:ring-gray-500 shadow-sm hover:shadow-lg rounded-lg'
      ),
      'rounded-outline': cn(
        'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-primary-500 rounded-lg',
        'hover:border-gray-400 hover:shadow-md'
      ),
      'rounded-dark': cn(
        'bg-gray-900 text-white hover:bg-gray-800 focus:ring-gray-500 shadow-sm hover:shadow-lg rounded-lg'
      ),
      'rounded-light': cn(
        'bg-gray-100 text-gray-900 hover:bg-gray-200 focus:ring-gray-500 shadow-sm hover:shadow-lg rounded-lg'
      ),
      link: cn(
        'text-primary-600 hover:text-primary-700 underline hover:no-underline bg-transparent p-0 font-medium'
      ),
      icon: cn(
        'p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900 focus:ring-gray-500',
        'hover:scale-[1.05] active:scale-[0.95] transition-all duration-200'
      ),
    };

    // Size classes
    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-sm',
      lg: 'px-6 py-3 text-base',
      xl: 'px-8 py-4 text-lg',
    };

    // Loading spinner
    const LoadingSpinner = () => (
      <svg
        className="animate-spin -ml-1 mr-2 h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );

    return (
      <button
        ref={ref}
        className={cn(baseClasses, variantClasses[variant], sizeClasses[size], className)}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        {...props}
      >
        {loading && <LoadingSpinner />}
        {!loading && leftIcon && <span className="mr-2">{leftIcon}</span>}
        {children}
        {!loading && rightIcon && <span className="ml-2">{rightIcon}</span>}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button };
